import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { CheckCircleFill, ShieldLockFill } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { getStoredAuthToken, getStoredAuthUser } from '../utils/authStorage';
import { userHasOrganization } from './setPasswordFlow';
import './auth-shared.css';

function errorMessageFromResponse(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail?.message) {
    return String(detail.message);
  }
  return error?.message || fallback;
}

function maskEmail(email: string): string {
  const [name, domain] = String(email || '').split('@');
  if (!name || !domain) {
    return email;
  }
  const visible = name.length <= 2 ? name[0] : `${name[0]}${name[name.length - 1]}`;
  return `${visible}${'*'.repeat(Math.max(1, name.length - visible.length))}@${domain}`;
}

const MFA_CODE_LENGTH = 6;

const MfaChallengeNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingMfa, verifyMfa, resendMfa, clearPendingMfa } = useAuth();

  const [otpDigits, setOtpDigits] = useState<string[]>(() => Array(MFA_CODE_LENGTH).fill(''));
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const emailLabel = useMemo(() => maskEmail(pendingMfa?.email || ''), [pendingMfa?.email]);
  const otpCode = useMemo(() => otpDigits.join(''), [otpDigits]);

  function focusOtpInput(index: number): void {
    const nextInput = otpInputRefs.current[index];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  function handleOtpChange(index: number, rawValue: string): void {
    const digits = rawValue.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH - index);

    setOtpDigits((previous) => {
      const next = [...previous];
      if (!digits) {
        next[index] = '';
        return next;
      }

      digits.split('').forEach((digit, offset) => {
        next[index + offset] = digit;
      });
      return next;
    });

    if (digits) {
      window.requestAnimationFrame(() => {
        focusOtpInput(Math.min(index + digits.length, MFA_CODE_LENGTH - 1));
      });
    }
  }

  function handleOtpKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (otpDigits[index]) {
        setOtpDigits((previous) => {
          const next = [...previous];
          next[index] = '';
          return next;
        });
        return;
      }

      if (index > 0) {
        setOtpDigits((previous) => {
          const next = [...previous];
          next[index - 1] = '';
          return next;
        });
        window.requestAnimationFrame(() => focusOtpInput(index - 1));
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusOtpInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < MFA_CODE_LENGTH - 1) {
      event.preventDefault();
      focusOtpInput(index + 1);
    }
  }

  function handleOtpPaste(index: number, event: React.ClipboardEvent<HTMLInputElement>): void {
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);
    if (!pastedDigits) {
      return;
    }

    event.preventDefault();
    const startIndex = pastedDigits.length === MFA_CODE_LENGTH ? 0 : index;
    setOtpDigits((previous) => {
      const next = [...previous];
      pastedDigits.split('').forEach((digit, offset) => {
        if (startIndex + offset < MFA_CODE_LENGTH) {
          next[startIndex + offset] = digit;
        }
      });
      return next;
    });
    window.requestAnimationFrame(() => {
      focusOtpInput(Math.min(startIndex + pastedDigits.length, MFA_CODE_LENGTH - 1));
    });
  }

  function finishNavigation(result: { must_change_password?: boolean; is_superadmin?: boolean }): void {
    const state = (location.state || {}) as any;
    const isSafeMcpRedirect = typeof state?.mcpRedirect === 'string'
      && /^http:\/\/(127\.0\.0\.1|localhost):\d+\//.test(state.mcpRedirect);
    if (isSafeMcpRedirect && state?.mcpState) {
      const token = getStoredAuthToken() || '';
      let orgId = '';
      try {
        const userObj = getStoredAuthUser<{ active_organization_id?: number | string; organization_id?: number | string }>() || {};
        orgId = String(userObj.active_organization_id ?? userObj.organization_id ?? '');
      } catch {
        orgId = '';
      }
      const callbackUrl = new URL(state.mcpRedirect);
      callbackUrl.searchParams.set('token', token);
      callbackUrl.searchParams.set('state', state.mcpState);
      callbackUrl.searchParams.set('org_id', orgId);
      callbackUrl.searchParams.set('email', pendingMfa?.email || '');
      window.location.href = callbackUrl.toString();
      return;
    }

    if (result?.must_change_password) {
      navigate('/set-password', { replace: true });
      return;
    }

    if (result?.is_superadmin) {
      navigate('/dashboard', { replace: true });
      return;
    }

    let hasOrg = false;
    try {
      hasOrg = userHasOrganization(getStoredAuthUser());
    } catch {
      hasOrg = false;
    }
    const requestedFrom = state?.from?.pathname;
    const fallback = hasOrg ? '/dashboard' : '/profile-setup';
    navigate(requestedFrom && requestedFrom !== '/set-password' ? requestedFrom : fallback, { replace: true });
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const normalizedCode = otpCode.replace(/\D/g, '');
    if (normalizedCode.length !== MFA_CODE_LENGTH) {
      setError('Enter the 6-digit verification code.');
      const firstEmptyIndex = otpDigits.findIndex((digit) => !digit);
      if (firstEmptyIndex >= 0) {
        focusOtpInput(firstEmptyIndex);
      }
      return;
    }

    setSubmitting(true);
    try {
      const result = await verifyMfa(normalizedCode, trustDevice, 'Browser');
      finishNavigation(result);
    } catch (err: any) {
      setError(errorMessageFromResponse(err, 'Verification failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setError(null);
    setMessage(null);
    setResending(true);
    try {
      const result = await resendMfa();
      setMessage(result.message || 'Verification code sent.');
    } catch (err: any) {
      setError(errorMessageFromResponse(err, 'Could not resend the verification code.'));
    } finally {
      setResending(false);
    }
  }

  function handleBackToLogin(): void {
    clearPendingMfa();
    navigate('/login', { replace: true });
  }

  if (!pendingMfa?.challenge_token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="text-center">
            <ShieldLockFill size={42} className="mb-3 text-primary" />
            <h2 className="auth-heading">Verification Required</h2>
            <p className="auth-subtitle">Please sign in again to request a new verification code.</p>
          </div>
          <button type="button" className="auth-submit-btn" onClick={handleBackToLogin}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="text-center">
          <ShieldLockFill size={42} className="mb-3 text-primary" />
          <h2 className="auth-heading">Verify your sign-in</h2>
          <p className="auth-subtitle">Enter the 6-digit code sent to {emailLabel || 'your email'}.</p>
        </div>

        {message && (
          <div className="auth-server-success">
            <CheckCircleFill size={14} />
            <span>{message}</span>
          </div>
        )}
        {error && <div className="auth-server-error">{error}</div>}

        <Form onSubmit={handleSubmit} noValidate>
          <div className="auth-form-group">
            <Form.Label className="auth-label" htmlFor="mfa-code-0">Verification code</Form.Label>
            <div className="mfa-code-inputs" role="group" aria-label="6-digit verification code">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  id={index === 0 ? 'mfa-code-0' : undefined}
                  ref={(element) => {
                    otpInputRefs.current[index] = element;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  value={digit}
                  onChange={(event) => handleOtpChange(index, event.target.value)}
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  onPaste={(event) => handleOtpPaste(index, event)}
                  onFocus={(event) => event.target.select()}
                  className="mfa-code-input"
                  aria-label={`Verification code digit ${index + 1}`}
                  maxLength={1}
                  disabled={submitting}
                  required
                />
              ))}
            </div>
          </div>

          <div className="auth-checkbox-row auth-checkbox-row-spaced">
            <input
              type="checkbox"
              id="mfa-trust-device"
              className="remember-me-checkbox-input"
              checked={trustDevice}
              onChange={(event) => setTrustDevice(event.target.checked)}
            />
            <label htmlFor="mfa-trust-device" className="auth-checkbox-visual-label">
              <span className="remember-me-indicator" aria-hidden="true">
                {trustDevice
                  ? <CheckCircleFill size={18} className="remember-me-indicator-check" />
                  : <span className="remember-me-indicator-circle" />}
              </span>
              <span className="auth-checkbox-copy">
                <span className="auth-checkbox-label">Remember this device</span>
                <span className="auth-checkbox-hint">Use only on a private device you control.</span>
              </span>
            </label>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={submitting}>
            {submitting ? <Spinner as="span" animation="border" size="sm" /> : 'Verify code'}
          </button>
        </Form>

        <div className="text-center mt-3">
          <button type="button" className="verify-resend-link" onClick={handleResend} disabled={resending}>
            {resending ? 'Sending...' : 'Resend code'}
          </button>
          <button type="button" className="verify-resend-link ms-3" onClick={handleBackToLogin}>
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

export default MfaChallengeNew;
